import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  useTheme,
  alpha,
  LinearProgress,
  Fab,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Science,
  People,
  CheckCircle,
  Tv as TvIcon,
  Error,
  Warning,
  NotificationsActive,
  Dashboard,
  MenuBook,
  Security,
  Assignment,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SampleStatistics } from '../services/samplesService';
import { useOptimizedStatistics } from '../hooks/useOptimizedStatistics';
import LabMonitoringPanel from '../components/Dashboard/LabMonitoringPanel';





interface QuickAction {
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  color: string;
}


const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const { 
    statistics, 
    derivedMetrics, 
    loading: statsLoading, 
    error: statsError, 
    refresh: refreshStatistics 
  } = useOptimizedStatistics(30000);
  
  const [error, setError] = useState<string | null>(null);

  const quickActions: QuickAction[] = [
    {
      title: 'Analytics Avancées',
      description: 'Voir les performances et métriques détaillées',
      icon: <AnalyticsIcon />,
      action: () => navigate('/analytics'),
      color: theme.palette.primary.main,
    },
    {
      title: 'Gestion Utilisateurs',
      description: 'Gérer les comptes et permissions',
      icon: <People />,
      action: () => navigate('/users'),
      color: theme.palette.secondary.main,
    },
    {
      title: 'Vue Laboratoires',
      description: 'Supervision générale des laboratoires',
      icon: <Science />,
      action: () => navigate('/laboratory'),
      color: theme.palette.success.main,
    },
    {
      title: 'Gestion Clients',
      description: 'Clients et projets',
      icon: <Assignment />,
      action: () => navigate('/clients'),
      color: theme.palette.warning.main,
    },
    {
      title: 'Certificats',
      description: 'Statut et génération des certificats',
      icon: <MenuBook />,
      action: () => navigate('/results/certificates'),
      color: theme.palette.info.main,
    },
    {
      title: 'Système & Sécurité',
      description: 'Paramètres système et sécurité',
      icon: <Security />,
      action: () => {}, // Placeholder for system settings
      color: theme.palette.error.main,
    },
  ];

  // Calculate real laboratory efficiency based on actual data
  const calculateLaboratoryEfficiency = (prepSamples: number, analyseSamples: number, completedSamples: number, totalSystemSamples: number) => {
    // If no samples, efficiency is 100% (no backlog)
    if (prepSamples + analyseSamples === 0) return 100;
    
    // Calculate efficiency based on throughput and queue congestion
    const totalLabSamples = prepSamples + analyseSamples;
    const queueFactor = Math.max(0, (10 - totalLabSamples) / 10); // Penalty for high queue
    const throughputFactor = totalSystemSamples > 0 ? completedSamples / totalSystemSamples : 0;
    
    // Combined efficiency: 70% queue management + 30% throughput
    const efficiency = (queueFactor * 70) + (throughputFactor * 30);
    return Math.min(100, Math.max(0, Math.round(efficiency)));
  };

  const getEfficiencyStatus = (efficiency: number) => {
    if (efficiency >= 90) return { status: 'Excellent', color: 'success' };
    if (efficiency >= 80) return { status: 'Bon', color: 'info' };
    if (efficiency >= 70) return { status: 'Moyen', color: 'warning' };
    return { status: 'Attention', color: 'error' };
  };

  const totalSamples = statistics?.total || 1;

  const buildLabStatus = (name: string, prepKey: string, analyseKey: string) => {
    const prep = statistics?.par_statut?.[prepKey] || 0;
    const analyse = statistics?.par_statut?.[analyseKey] || 0;
    const completed = statistics?.par_statut?.PRET || 0;
    const eff = calculateLaboratoryEfficiency(prep, analyse, completed, totalSamples);
    return { name, samples: prep + analyse, efficiency: eff, ...getEfficiencyStatus(eff) };
  };

  const laboratoryStatus = [
    buildLabStatus('SAA (Absorption Atomique)', 'LABORATOIRE_SAA_PREP_CHIMIQUE', 'LABORATOIRE_SAA_ANALYSE'),
    buildLabStatus('ICP (Spectroscopie)', 'LABORATOIRE_ICP_PREP_CHIMIQUE', 'LABORATOIRE_ICP_ANALYSE'),
    buildLabStatus('AU (Analyse Or)', 'LABORATOIRE_AU_PREP_CHIMIQUE', 'LABORATOIRE_AU_ANALYSE'),
    buildLabStatus('Volumétrie', 'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE', 'LABORATOIRE_VOLUMETRIE_ANALYSE'),
  ];

  const recentAlerts = [
    ...(((statistics?.par_statut?.PREPARATION_MECANIQUE || 0) > 5) ? [{
      type: 'warning',
      title: 'Goulot d\'étranglement - Préparation Mécanique',
      description: `${statistics?.par_statut?.PREPARATION_MECANIQUE} échantillons en attente`,
      time: 'Il y a 15 min',
    }] : []),
    ...(((statistics?.par_statut?.VALIDATION_SERVICE || 0) > 10) ? [{
      type: 'error',
      title: 'Arriéré de validation important',
      description: `${statistics?.par_statut?.VALIDATION_SERVICE} échantillons en validation`,
      time: 'Il y a 32 min',
    }] : []),
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Dashboard />
          Tableau de Bord Administrateur
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Bienvenue, {user?.firstName} {user?.lastName} - Vue d'ensemble système AFRILAB
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Lab Monitoring Panel */}
      <LabMonitoringPanel />

      {/* Quick Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Actions Rapides</Typography>
          <Grid container spacing={2}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' },
                    border: `1px solid ${alpha(action.color, 0.2)}`,
                  }}
                  onClick={action.action}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: action.color }}>
                      {action.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">{action.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {action.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Laboratory Status */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>État des Laboratoires</Typography>
              <List>
                {laboratoryStatus.map((lab, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <Science />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={lab.name}
                        secondary={
                          <Box>
                            <Typography variant="caption">
                              {lab.samples} échantillons • Efficacité: {lab.efficiency}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={lab.efficiency}
                              sx={{ mt: 1 }}
                              color={lab.color as any}
                            />
                          </Box>
                        }
                      />
                      <Chip 
                        label={lab.status} 
                        color={lab.color as any} 
                        size="small" 
                      />
                    </ListItem>
                    {index < laboratoryStatus.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts & Notifications */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsActive />
                Alertes Récentes
              </Typography>
              {recentAlerts.length > 0 ? (
                <List>
                  {recentAlerts.map((alert, index) => (
                    <ListItem key={index} divider>
                      <ListItemIcon>
                        {alert.type === 'error' ? (
                          <Error color="error" />
                        ) : (
                          <Warning color="warning" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={alert.title}
                        secondary={
                          <Box>
                            <Typography variant="caption">{alert.description}</Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {alert.time}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Aucune alerte système
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tous les systèmes fonctionnent normalement
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* TV Dashboard Floating Action Button */}
      <Fab
        color="secondary"
        aria-label="TV Dashboard"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            transform: 'scale(1.1)',
          },
          transition: 'all 0.3s ease',
          zIndex: 1000,
        }}
        onClick={() => window.open('/tv-dashboard', '_blank')}
      >
        <TvIcon />
      </Fab>
    </Box>
  );
};

export default AdminDashboard; 